export type RoleAwareUser =
  | {
      email?: string | null;
      roles?: string[] | null;
    }
  | null
  | undefined;

const coachRoles = new Set(['coach']);
const driveRequiredRoles = new Set(['coach', 'coach_admin']);
const adminRoles = new Set(['admin', 'super_admin']);
const memberRoles = new Set(['member']);

function normalizePathname(path: string | undefined): string {
  if (!path) {
    return '/';
  }

  const pathname = path.split(/[?#]/, 1)[0]?.trim() || '/';
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function normalizeRoles(roles: string[] | null | undefined): string[] {
  if (!Array.isArray(roles)) {
    return [];
  }

  return roles
    .map((role) => role.trim().toLowerCase())
    .filter((role, index, allRoles) => role.length > 0 && allRoles.indexOf(role) === index);
}

export function isCoachUser(user: RoleAwareUser): boolean {
  const roles = normalizeRoles(user?.roles);
  return roles.some((role) => coachRoles.has(role));
}

export function shouldRequireGoogleDriveForUser(user: RoleAwareUser): boolean {
  const roles = normalizeRoles(user?.roles);
  return roles.some((role) => driveRequiredRoles.has(role));
}

export function isAdminUser(user: RoleAwareUser): boolean {
  const roles = normalizeRoles(user?.roles);
  return roles.some((role) => adminRoles.has(role));
}

export function isFullAccessUser(user: RoleAwareUser): boolean {
  return isCoachUser(user) || isAdminUser(user);
}

export function isMemberUser(user: RoleAwareUser): boolean {
  const roles = normalizeRoles(user?.roles);
  return roles.some((role) => memberRoles.has(role)) && !isFullAccessUser(user);
}

export function isNoAccessUser(user: RoleAwareUser): boolean {
  return !isFullAccessUser(user) && !isMemberUser(user);
}

export function isMemberSafePath(path: string): boolean {
  const pathname = normalizePathname(path);

  // Keep this allowlist explicit and narrow. If a route is truly member-safe,
  // add it here and cover it with a routing test. The `/members/...` and
  // `/coach/member/...` route families remain coach-owned even when the
  // underlying artifact is shareable.
  if (pathname === '/artifacts' || pathname === '/artifacts/generating') {
    return true;
  }

  if (/^\/artifacts\/[^/]+$/.test(pathname)) {
    return true;
  }

  // OAuth authorization endpoint for Stytch Connected Apps (e.g. Freshmint MCP).
  // Must be member-safe so post-login redirect preserves the OAuth query params.
  if (pathname === '/oauth/authorize') {
    return true;
  }

  return false;
}

export function defaultAuthenticatedPath(user: RoleAwareUser): string {
  if (isCoachUser(user)) {
    return '/members';
  }

  if (isAdminUser(user)) {
    return '/admin';
  }

  if (isMemberUser(user)) {
    return '/artifacts';
  }

  return '/no-access';
}

export function canAccessAuthenticatedPath(user: RoleAwareUser, path: string): boolean {
  const pathname = normalizePathname(path);

  if (isFullAccessUser(user)) {
    return pathname !== '/artifacts' && pathname !== '/no-access';
  }

  if (isMemberUser(user)) {
    return isMemberSafePath(pathname);
  }

  return pathname === '/no-access';
}

export function resolveAuthenticatedPath(user: RoleAwareUser, requestedPath?: string): string {
  if (!requestedPath) {
    return defaultAuthenticatedPath(user);
  }

  const normalizedRequestedPath = requestedPath;

  return canAccessAuthenticatedPath(user, normalizedRequestedPath)
    ? normalizedRequestedPath
    : defaultAuthenticatedPath(user);
}
